/**
 * Словари конфигуратора — Block 6 Phase 1 (PRD `docs/prd/v4/08-block-6-configurator.md` §«Экран 2.6.1» + ADR-0015 §Q5).
 *
 * Используется:
 *   - UI Экран 2.6.1 (`/configurator`) — TypePickerScreen рендерит 9 type cards.
 *   - UI Экран 2.6.2 (`/configurator/:type`) — header label + URL guard на невалидный type.
 *   - Zod-схемы в `contracts/configurator.ts` (FurnitureTypeSchema, ConfiguratorSlotIdSchema).
 *   - Phase 2 SVG-шаблоны: маппинг `FurnitureType` → визуальный контур.
 *   - Phase 3 selection mode: `ConfiguratorSlotId` → query-param `?slot={id}`.
 *
 * Все значения сохраняются как есть — без нормализации (P-001 spirit).
 *
 * ── 9 типов мебели (PRD §«Экран 2.6.1») ────────────────────────────────
 * Phase 9+ admin extension: PRD §«Контуры (проекты) мебели для конфигуратора»
 * допускает добавление новых типов через админ-панель + загрузку contour-картинки
 * в R2. Phase 1 фиксирует начальный набор; расширение enum'а — non-breaking
 * optional (новый тип появится в backend, фронт fallback'нет на 'other').
 *
 * ── 5 слотов (PRD §«Экран 2.6.2») ──────────────────────────────────────
 * 4 обязательных (facade / countertop / corpus / handles) + 1 опциональный
 * (hooks). `isComplete()` в `configuratorStore` (Step 2) исключает hooks из
 * проверки готовности к финальной карточке 2.6.8 (Phase 4).
 */

// ── 9 типов мебели (порядок = порядок отображения в TypePickerScreen) ───
export const FURNITURE_TYPES = Object.freeze([
  'kitchen',
  'hallway',
  'wardrobe',
  'bedroom',
  'living_room',
  'children',
  'bathroom',
  'balcony',
  'other',
] as const);
export type FurnitureType = (typeof FURNITURE_TYPES)[number];

/**
 * Русские лейблы для UI (TypePickerScreen + ConfiguratorScreen header).
 * Один источник истины — здесь; LinguiJS-локализация на UZ/KZ — отдельная фаза.
 */
export const FURNITURE_TYPE_LABELS_RU: Record<FurnitureType, string> = {
  kitchen: 'Кухня',
  hallway: 'Прихожая',
  wardrobe: 'Гардероб',
  bedroom: 'Спальня',
  living_room: 'Гостиная',
  children: 'Детская',
  bathroom: 'Санузел',
  balcony: 'Балкон',
  other: 'Прочее',
};

/**
 * Имена lucide-react иконок для type cards. Step 3 импортирует
 * фактические компоненты `import { ChefHat, ... } from 'lucide-react'` и
 * рендерит через локальный `ICON_MAP`. Phase 1 хранит имена как строки —
 * без runtime-зависимости от lucide в `packages/shared`.
 *
 * Phase 2 может заменить lucide-fallback на свои SVG-контуры мебели в
 * `apps/web/src/features/configurator/visualizations/`.
 */
export const FURNITURE_TYPE_ICONS: Record<FurnitureType, string> = {
  kitchen: 'ChefHat',
  hallway: 'DoorOpen',
  wardrobe: 'Shirt',
  bedroom: 'Bed',
  living_room: 'Sofa',
  children: 'Baby',
  bathroom: 'Bath',
  balcony: 'Trees',
  other: 'Package',
};

// ── 5 слотов конфигуратора (порядок = порядок отображения 2.6.2) ────────
export const CONFIGURATOR_SLOT_IDS = Object.freeze([
  'facade',
  'countertop',
  'corpus',
  'handles',
  'hooks',
] as const);
export type ConfiguratorSlotId = (typeof CONFIGURATOR_SLOT_IDS)[number];

/**
 * Русские лейблы слотов (PRD §«Экран 2.6.2»).
 */
export const CONFIGURATOR_SLOT_LABELS_RU: Record<ConfiguratorSlotId, string> = {
  facade: 'Фасад',
  countertop: 'Столешница',
  corpus: 'Корпус',
  handles: 'Ручки',
  hooks: 'Крючки',
};

/**
 * Опциональные слоты — `hooks` per PRD §«Слот "Крючки"».
 * `isComplete()` в `configuratorStore` (Step 2) исключает их из проверки
 * готовности к Phase 4 финальной карточке 2.6.8.
 *
 * Если в будущем появятся ещё опциональные слоты — добавляем сюда, не
 * хардкодим в store.
 */
export const CONFIGURATOR_OPTIONAL_SLOT_IDS: readonly ConfiguratorSlotId[] = Object.freeze([
  'hooks',
] as const);
